export default class FaqBlock {
	/**
	 * The breakpoint between desktop and mobile
	 */
	desktopBreakpoint;

	/**
	 * The block container element.
	 */
	faqBlockContainer;

	/**
	 * The currently chosen question.
	 */
	currentQuestion;

	/**
	 * The document and window the block is in, which are the editor iframe's in an iframed editor.
	 */
	doc;
	view;

	/**
	 * The containers which hold the current question and answer.
	 */
	desktopAnswerContainers;

	/**
	 * constructor
	 *
	 * Initial setup
	 *
	 * @param  {HTMLElement} faqContainer The faq block container element
	 * @param  {number} breakpointWidth The breakpoint width for the accordion behaviour
	 */
	constructor( faqContainer, breakpointWidth = 992 ) {
		this.desktopBreakpoint = breakpointWidth;
		this.faqBlockContainer = faqContainer;
		this.doc = faqContainer.ownerDocument || document;
		this.view = this.doc.defaultView || window;
		this.desktopAnswerContainers = {
			answerTitle: faqContainer.querySelector( '.answer__title' ),
			answerContent: faqContainer.querySelector( '.answer__content' ),
		};
		this.currentQuestion = false;

		if ( this.isMobile() || ! this.enableDesktop() ) {
			if ( this.expandFirstPost() ) {
				const firstQuestion = faqContainer.querySelector( '.question-list__item:first-child' );
				this.toggleQuestionOnMobile( firstQuestion );
			}
		} else if ( this.enableDesktop() ) {
			if ( this.expandFirstPost() ) {
				const firstQuestion = faqContainer.querySelector( '.question-list__item:first-child' );
				this.toggleQuestionOnDesktop( firstQuestion );
			}
		}

		this.addEventListenersToFaqs();
		this.observeLoadMoreButton();
	}

	/**
	 * addEventListenersToFaqs
	 *
	 * Calls the addEventListenersToFaq on all faqs
	 *
	 */
	addEventListenersToFaqs() {
		const clickableQuestions = this.faqBlockContainer.querySelectorAll( '.question-list__item-title' );
		clickableQuestions.forEach( clickableQuestion => {
			this.addEventListenersToFaq( clickableQuestion );
		} );
	}

	/**
	 * addEventListenersToFaq
	 *
	 * @param  {HTMLElement} faqItem The element on which we want to add a event listener.
	 */
	addEventListenersToFaq( faqItem ) {
		// The sweep above re-runs after Load More, so skip titles already bound.
		if ( faqItem._faqToggleBound ) {
			return;
		}
		faqItem._faqToggleBound = true;

		faqItem.addEventListener( 'click', e => {
			const clickedQuestion = e.currentTarget.parentElement;

			if ( this.isMobile() || ! this.enableDesktop() ) {
				this.toggleQuestionOnMobile( clickedQuestion );
			} else if ( this.enableDesktop() ) {
				this.toggleQuestionOnDesktopWithScrollAndTransition( clickedQuestion );
			}
		} );

		faqItem.addEventListener( 'keydown', e => {
			if ( e.key === 'Enter' || e.key === ' ' ) {
				// Prevent the default action (like firing a click event or scrolling the page)
				e.preventDefault();

				const clickedQuestion = e.currentTarget.parentElement;

				if ( this.isMobile() || ! this.enableDesktop() ) {
					this.toggleQuestionOnMobile( clickedQuestion );
				} else if ( this.enableDesktop() ) {
					this.toggleQuestionOnDesktopWithScrollAndTransition( clickedQuestion );
				}
			}
		} );
	}

	/**
	 * toggleQuestionOnMobile
	 *
	 * Handles the click on a question on mobile
	 *
	 * @param  {HTMLElement} chosenQuestion The clicked question
	 */
	toggleQuestionOnMobile( chosenQuestion ) {
		this.toggleSelectedQuestionClasses( chosenQuestion );

		this.fetchAndInsertMobileContent( chosenQuestion );

		// Update the currentQuestion to the clicked question if it's a new one or null if the user is closing the currently open one.
		this.currentQuestion = chosenQuestion === this.currentQuestion ? null : chosenQuestion;
	}

	/**
	 * toggleQuestionOnDesktop
	 *
	 * Handles the click on a question on desktop
	 *
	 * @param  {HTMLElement} chosenQuestion The clicked question
	 */
	toggleQuestionOnDesktop( chosenQuestion ) {
		// Bail if the same question is clicked multiple times on desktop.
		if ( chosenQuestion === this.currentQuestion ) {
			return;
		}

		this.toggleSelectedQuestionClasses( chosenQuestion );

		this.fetchAndInsertDesktopContent( chosenQuestion );

		this.currentQuestion = chosenQuestion;
	}

	/**
	 * fetchAndInsertMobileContent
	 *
	 * Replaces the content when a new question is clicked on desktop
	 *
	 * @param  {HTMLElement} chosenQuestion The clicked question
	 */
	fetchAndInsertMobileContent( chosenQuestion ) {
		// if no posts selected via manual selection or contextual posts not found, return to prevent a console error
		if ( ! chosenQuestion ) {
			return;
		}

		if ( this.currentQuestion ) {
			// Remove the content and class from the previously selected question
			const currentQuestionContentContainer = this.currentQuestion.querySelector( '.question-list__accordion-content' );

			currentQuestionContentContainer.setAttribute( 'aria-expanded', false );
		}

		if ( chosenQuestion !== this.currentQuestion ) {
			// Add the content and class to the chosen question
			const chosenQuestionContentContainer = chosenQuestion.querySelector( '.question-list__accordion-content' );

			chosenQuestionContentContainer.setAttribute( 'aria-expanded', true );
		}

		// do this in case user clicks question on mobile and see's a blank Q+A column when switching to landscape or a larger viewport size
		this.fetchAndInsertDesktopContent( chosenQuestion );
	}

	/**
	 * fetchAndInsertDesktopContent
	 *
	 * Replaces the content when a new question is clicked on desktop
	 *
	 * @param {HTMLElement} chosenQuestion The clicked question
	 */
	fetchAndInsertDesktopContent( chosenQuestion ) {
		const { answerTitle, answerContent } = this.desktopAnswerContainers;

		if ( ! answerTitle || ! answerContent ) {
			return;
		}

		answerTitle.innerHTML = chosenQuestion?.querySelector( '.question-list__item-title' ).textContent;

		// clear answerContent.innerHTML then getChosenQuestionAnswer
		answerContent.innerHTML = '';
		answerContent.innerHTML = this.getChosenQuestionAnswer( chosenQuestion );
	}

	/**
	 * toggleSelectedQuestionClasses
	 *
	 * Toggles the question-list__item--current on the clicked and/or previously active question
	 *
	 * @param {HTMLElement} chosenQuestion The clicked question
	 */
	toggleSelectedQuestionClasses( chosenQuestion ) {
		if ( this.currentQuestion ) {
			// Remove the content and class from the previously selected question
			this.currentQuestion.classList.remove( 'question-list__item--current' );

			// update aria states
			const button = this.currentQuestion.querySelector( '.question-list__item-title' );
			const content = this.currentQuestion.querySelector( '.question-list__accordion-content' );
			const innerContent = content?.querySelector( '.question-list__accordion-content-inner' );

			button?.setAttribute( 'aria-expanded', 'false' );
			content?.setAttribute( 'aria-hidden', 'true' );
			innerContent?.setAttribute( 'inert', '' );
		}

		if ( ! chosenQuestion ) {
			return;
		}

		// Don't add the class back if it's the same accordion closing on mobile
		if ( ( chosenQuestion !== this.currentQuestion ) ) {
			chosenQuestion.classList.add( 'question-list__item--current' );

			const button = chosenQuestion.querySelector( '.question-list__item-title' );
			const content = chosenQuestion.querySelector( '.question-list__accordion-content' );
			const innerContent = content?.querySelector( '.question-list__accordion-content-inner' );

			button?.setAttribute( 'aria-expanded', 'true' );
			content?.setAttribute( 'aria-hidden', 'false' );
			innerContent?.removeAttribute( 'inert' );
		}
	}

	/**
	 * getChosenQuestionAnswer
	 *
	 * Fetches, sanitizes and returns the answer of the chosen question
	 *
	 * @param  {HTMLElement} chosenQuestion The clicked question
	 */
	getChosenQuestionAnswer( chosenQuestion ) {
		return chosenQuestion.dataset?.answer ?? '';
	}

	/**
	 * isMobile
	 *
	 * Check wether the current viewport width is less than then desktopBreakpoint
	 *
	 * @return {boolean} If the current device is mobile-sized
	 */
	isMobile() {
		return this.desktopBreakpoint >= this.view.innerWidth;
	}

	/**
	 * enableDesktop
	 *
	 * Check if enableDesktop layout option is active
	 *
	 * @return {boolean} If the layout should have two column layout enabled
	 */
	enableDesktop() {
		return this.faqBlockContainer.classList.contains( 'enable-desktop' );
	}

	/**
	 * expandFirstPost
	 *
	 * Check if expandFirstPost is enabled
	 *
	 * @return {boolean} If the first FAQ post should be expanded
	 */
	expandFirstPost() {
		return this.faqBlockContainer.classList.contains( 'expand-first-faq' );
	}

	/**
	 * Trigger page view transition and scroll to answer when finished.
	 *
	 * @param {HTMLElement} clickedQuestion The clicked question.
	 * @return {boolean} If the layout should have two column layout enabled
	 */
	async toggleQuestionOnDesktopWithScrollAndTransition( clickedQuestion ) {
		// first get this FAQ Block
		const thisFaqBlock = clickedQuestion.closest( '.wp-block-qala-blocks-faq-block' );
		// then get this FAQ Block's answer container
		const thisFaqBlockAnswer = thisFaqBlock.querySelector( '.wp-block-qala-blocks-faq-block__answer-column' );
		const header = this.doc.querySelector( '.site-header' );
		// offset site header height, plus 15 for a bit of space
		const headerHeight = header?.offsetHeight + 15 || 15;

		// Get top offset of the block, subtract header height
		const view = this.view;
		const targetY = thisFaqBlockAnswer.getBoundingClientRect().top + view.scrollY - headerHeight;

		// Start smooth scroll
		view.scrollTo( { top: targetY, behavior: 'smooth' } );

		// Wait for scroll to settle
		await new Promise( resolve => {
			let lastY = view.scrollY;
			let sameCount = 0;

			function checkScroll() {
				const currentY = view.scrollY;

				if ( currentY === lastY ) {
					sameCount++;
					if ( sameCount > 5 ) {
						resolve();
						return;
					}
				} else {
					sameCount = 0;
					lastY = currentY;
				}

				view.requestAnimationFrame( checkScroll );
			}

			checkScroll();
		} );

		// Scroll has finished, now run transition if supported
		if ( ! this.doc.startViewTransition ) {
			this.toggleQuestionOnDesktop( clickedQuestion );
		} else {
			this.doc.startViewTransition( () => this.toggleQuestionOnDesktop( clickedQuestion ) );
		}
	}

	/**
	 * observeLoadMoreButton
	 *
	 * Observes the FAQ block container for DOM changes and attempts to bind
	 * the Load More button click event both initially and whenever the button is replaced.
	 *
	 * This ensures AJAX loading remains functional after each Load More action.
	 */
	observeLoadMoreButton() {
		// Attempt to bind the initial Load More button on first render
		this.bindLoadMoreButton();

		// Observe future DOM changes to rebind after replacement
		const observer = new MutationObserver( () => {
			this.bindLoadMoreButton();
		} );

		observer.observe( this.faqBlockContainer, { childList: true, subtree: true } );
	}

	/**
	 * bindLoadMoreButton
	 *
	 * Locates the .faq-load-more button within the block container and
	 * binds a click event to load the next page of FAQs via the REST API.
	 *
	 * Prevents multiple bindings by marking the button with _faqClickBound.
	 * Replaces the button with a new one if more results exist, or removes it if done.
	 *
	 * Also appends new FAQ items and re-applies event listeners to them.
	 */
	bindLoadMoreButton() {
		const button = this.faqBlockContainer.querySelector( '.faq-load-more' );

		if ( button && ! button._faqClickBound ) {
			button._faqClickBound = true;

			button.addEventListener( 'click', event => {
				button.setAttribute( 'disabled', true );
				event.preventDefault();

				const page = parseInt( this.faqBlockContainer.dataset.page || '1', 10 );
				const perPage = parseInt( this.faqBlockContainer.dataset.perPage || '5', 10 );
				const nextPage = page + 1;
				const offset = page * perPage;
				const categoryData = this.faqBlockContainer.dataset.categories || '';
				const categories = categoryData ? categoryData.split( ',' ).map( id => parseInt( id, 10 ) ) : [];
				const hasManualSelection = this.faqBlockContainer.dataset.hasManualSelection === '1';
				const postIdsData = this.faqBlockContainer.dataset.postIds || '';
				const postIds = postIdsData ? postIdsData.split( ',' ).map( id => parseInt( id, 10 ) ) : [];
				const questionSize = this.faqBlockContainer.dataset.questionSize || '';

				const url = new URL( '/wp-json/qala/v1/faq-load-more', window.location.origin );
				url.searchParams.set( 'page', nextPage );
				url.searchParams.set( 'per_page', perPage );
				url.searchParams.set( 'offset', offset );
				url.searchParams.set( 'categories', categories.join( ',' ) );
				url.searchParams.set( 'has_manual_selection', hasManualSelection ? '1' : '0' );
				if ( hasManualSelection ) {
					url.searchParams.set( 'post_ids', postIds.join( ',' ) );
				}
				if ( questionSize ) {
					url.searchParams.set( 'question_size', questionSize );
				}

				window.fetch( url )
					.then( res => res.json() )
					.then( ( { html, hasMore } ) => {
						const tempDiv = document.createElement( 'div' );
						tempDiv.innerHTML = html;

						const newItems = tempDiv.querySelectorAll( '.question-list__item' );
						const targetList = this.faqBlockContainer.querySelector( '.question-column__question-list' );

						if ( ! document.startViewTransition ) {
							newItems.forEach( item => targetList.appendChild( item ) );
							this.addEventListenersToFaqs();
						} else {
							const transition = document.startViewTransition( () => {
								newItems.forEach( item => targetList.appendChild( item ) );
							} );

							// trigger the eventlisteners after the transition has finished:
							transition.finished.then( () => {
								this.addEventListenersToFaqs();
							} );
						}

						const oldButton = this.faqBlockContainer.querySelector( '.faq-load-more' );
						if ( hasMore ) {
							// Replace with fresh new Load More button from tempDiv
							const newButton = tempDiv.querySelector( '.faq-load-more' );
							if ( newButton ) {
								if ( ! document.startViewTransition ) {
									oldButton.replaceWith( newButton );
								} else {
									const transition = document.startViewTransition( () => {
										oldButton.replaceWith( newButton );
									} );

									// trigger the eventlisteners after the transition has finished:
									transition.finished.then( () => {
										this.bindLoadMoreButton(); // rebind
									} );
								}
							}
						} else {
							oldButton.remove();
						}

						// update page for next button click.
						this.faqBlockContainer.dataset.page = nextPage;
					} );
			} );
		}
	}
}
